import type { RailQuote } from "../types/payments";

// Why the Instant rail in the Transfer sheet can't be picked right now — the
// explainer a tap on the grey row opens (1.5.2). The server only ever shows
// the rail to talent who pass every rule except, at most, the debit card
// (lib/payouts/cashoutOptions.js quoteInstantRail), so these are ALL the ways
// it can be grey. It sends one English line per case (unavailableReason),
// no machine key, so the line itself is the key — matched exactly here.
// Anything else (a newer server's line) gets the generic explainer.
export type InstantUnavailableReason =
  | "needsCard" // no instant-capable debit card on the Stripe account
  | "noFunds" // Stripe has cleared none of the balance for instant (also: under Stripe's minimum, currency not priced)
  | "partFunds" // Stripe has cleared less than this amount for instant
  | "tooSmall" // after the instant fee, under Stripe's per-payout minimum
  | "dailyLimit" // our per-talent daily instant cap is used up
  | "notNow"; // instant fee not configured on our side, or a line this build doesn't know

const BY_SERVER_LINE: Record<string, InstantUnavailableReason> = {
  "Add a debit card": "needsCard",
  "No card funds yet": "noFunds",
  "Not enough card funds yet": "partFunds",
  "Too small for instant": "tooSmall",
  "Daily limit reached. Try tomorrow.": "dailyLimit",
  "Not available right now": "notNow",
};

/** The reason behind an unavailable instant rail; null when it can be picked. */
export function instantUnavailableReason(rail: Pick<RailQuote, "available" | "needsCard" | "unavailableReason">): InstantUnavailableReason | null {
  if (rail.needsCard) return "needsCard";
  if (rail.available) return null;
  return BY_SERVER_LINE[(rail.unavailableReason || "").trim()] || "notNow";
}

/**
 * The three explainers the founder kept (1.5.2 review): every reason folds
 * into one of them. Only the explanation — what can be picked is still the
 * server's call (rail.available), untouched here.
 *   amount      — not enough of THIS amount is cleared for instant (no funds, part, under the minimum)
 *   unavailable — instant is off for now (daily limit, not available, anything new): the default
 *   needsCard   — no debit card yet
 */
export type InstantExplainer = "amount" | "unavailable" | "needsCard";

const EXPLAINER: Record<InstantUnavailableReason, InstantExplainer> = {
  needsCard: "needsCard",
  noFunds: "amount",
  partFunds: "amount",
  tooSmall: "amount",
  dailyLimit: "unavailable",
  notNow: "unavailable",
};

export const instantExplainerFor = (reason: InstantUnavailableReason): InstantExplainer => EXPLAINER[reason];

/** True when the server's line is one this build translates (the row then shows it in the viewer's language). */
export const isKnownInstantLine = (line: string | null | undefined) => !!line && line.trim() in BY_SERVER_LINE;
